<template>
  <div v-if="isOpen" class="modal fade show head-sheet-modal" aria-labelledby="budgetHeadSheetTitle">
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content hs-shell">
        <div class="hs-header">
          <div class="hs-brand">
            <span class="hs-brand-icon"><i class="fas fa-file-excel"></i></span>
            <div class="hs-brand-text">
              <h3 id="budgetHeadSheetTitle" class="hs-title mb-0">Import Budget from Excel</h3>
              <p class="hs-subtitle mb-0">
                <span class="hs-tab-chip">{{ sheet.tab.name }}</span>
                <span v-if="sheet.budgetName" class="hs-budget-name" :title="sheet.budgetName">{{ sheet.budgetName }}</span>
                <span class="hs-subtitle-note">Nothing is saved until you press Apply.</span>
              </p>
            </div>
          </div>
          <ol class="hs-stepper mb-0">
            <li v-for="(item, index) in steps" :key="item.id" class="hs-step" :class="stepClass(index)">
              <span class="hs-step-dot">
                <span v-if="isStepDone(index)"><i class="fas fa-check"></i></span>
                <span v-if="! isStepDone(index)">{{ index + 1 }}</span>
              </span>
              <span class="hs-step-text">
                <span class="hs-step-name">{{ item.name }}</span>
                <span class="hs-step-caption">{{ item.caption }}</span>
              </span>
            </li>
          </ol>
          <button type="button" class="hs-close" aria-label="Close" :disabled="sheet.isApplying" @click="requestLeave('close')">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="hs-body">
          <div v-if="sheet.step === 'upload'" class="hs-narrow">
            <BudgetHeadSheetUpload
                :tab="sheet.tab.id"
                :isReading="sheet.isReading"
                :isExporting="sheet.isExporting"
                :fileName="fileName"
                @onFileSelected="sheet.readSheet"
                @onExport="sheet.exportSheet"
            />
          </div>
          <BudgetHeadSheetReview
              v-if="sheet.step === 'review'"
              :key="sheet.review.signature"
              :review="sheet.review"
              :busyHeads="sheet.busyHeads"
              :editedHeads="sheet.editedHeads"
              :canEditField="sheet.isEditableField"
              @onEditCell="sheet.editCell"
              @onUndoRow="sheet.undoRow"
          />
          <div v-if="sheet.step === 'done'" class="hs-narrow">
            <div class="hs-done">
              <span class="hs-done-icon"><i class="fas fa-check"></i></span>
              <h3 class="hs-done-title">Budget updated</h3>
              <p class="hs-muted mb-0">The changes from <strong>{{ fileName }}</strong> are saved.</p>
              <div class="hs-done-tiles">
                <div class="hs-done-tile">
                  <span class="hs-tile-label">Added</span>
                  <span class="hs-tile-value text-success">{{ sheet.result.created }}</span>
                  <span class="hs-tile-hint">new head(s)</span>
                </div>
                <div class="hs-done-tile">
                  <span class="hs-tile-label">Updated</span>
                  <span class="hs-tile-value text-primary">{{ sheet.result.updated }}</span>
                  <span class="hs-tile-hint">existing head(s)</span>
                </div>
                <div v-if="summary" class="hs-done-tile">
                  <span class="hs-tile-label">{{ totalTile.label }}</span>
                  <span class="hs-tile-value">{{ figure(totalTile.value) }}</span>
                  <span class="hs-tile-diff" :class="totalDifference.tone">{{ totalDifference.text }}</span>
                  <span class="hs-tile-hint">was {{ figure(totalTile.was) }}</span>
                </div>
              </div>
              <div class="hs-done-actions">
                <button type="button" class="btn btn-outline-primary" @click="sheet.reset">
                  <i class="fas fa-redo me-50"></i>Import another file
                </button>
                <button type="button" class="btn btn-primary" @click="closeModal">Done</button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sheet.step !== 'done'" class="hs-footer">
          <div v-if="sheet.step === 'review'" class="hs-file-chip">
            <i class="fas fa-file-excel hs-file-icon"></i>
            <span class="hs-file-name" :title="fileName">{{ fileName }}</span>
            <button type="button" class="hs-link" :disabled="sheet.isApplying" @click="requestLeave('change')">Change file</button>
          </div>
          <div class="hs-footer-actions">
            <span v-if="isChecking" class="hs-checking">
              <span class="spinner-border spinner-border-sm me-50" role="status"></span>Checking your change...
            </span>
            <span v-if="! isChecking && sheet.step === 'review' && applyHint" class="hs-hint">
              <i class="fas fa-info-circle me-25"></i>{{ applyHint }}
            </span>
            <button type="button" class="btn btn-outline-secondary" :disabled="sheet.isApplying" @click="requestLeave('close')">
              Cancel
            </button>
            <button v-if="sheet.step === 'review'" type="button" class="btn btn-primary hs-apply" :disabled="! sheet.canApply" @click="sheet.applySheet">
              <span v-if="sheet.isApplying" class="spinner-border spinner-border-sm me-50" role="status"></span>
              Apply changes
              <span class="hs-apply-count">{{ sheet.changeCount }}</span>
            </button>
          </div>
        </div>

        <div v-if="pendingAction" class="hs-confirm-backdrop">
          <div class="hs-confirm" role="alertdialog" aria-labelledby="hsConfirmTitle">
            <span class="hs-confirm-icon"><i class="fas fa-exclamation-triangle"></i></span>
            <h4 id="hsConfirmTitle" class="hs-confirm-title">Discard your changes?</h4>
            <p class="hs-confirm-text mb-0">
              You changed {{ editedCount }} row(s) in this review. {{ leaveTexts[pendingAction] }} The saved budget is not touched.
            </p>
            <div class="hs-confirm-actions">
              <button ref="keepButton" type="button" class="btn btn-outline-secondary" @click="keepEditing">Keep editing</button>
              <button type="button" class="btn btn-danger" @click="confirmLeave">Discard</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import BudgetHeadSheetUpload from './BudgetHeadSheetUpload.vue'
import BudgetHeadSheetReview from './BudgetHeadSheetReview.vue'

const props = defineProps({
  sheet: { type: Object, required: true }
})

const { commaFormat } = figureFormatter()

const steps = [
  { id: 'upload', name: 'Upload', caption: 'Choose file' },
  { id: 'review', name: 'Review', caption: 'Check & edit' },
  { id: 'done', name: 'Done', caption: 'Saved' }
]

const leaveTexts = {
  close: 'If you close now, they are lost.',
  change: 'If you choose another file, they are lost.'
}

const typingTags = ['INPUT', 'TEXTAREA', 'SELECT']

const isOpen = ref(false)
const pendingAction = ref('')
const keepButton = ref(null)

const activeIndex = computed(() => steps.findIndex(item => item.id === props.sheet.step))

const fileName = computed(() => {
  if (! props.sheet.file) return ''

  return props.sheet.file.name
})

const summary = computed(() => {
  if (! props.sheet.review) return null

  return props.sheet.review.summary
})

const totalTile = computed(() => {
  if (props.sheet.review.layout.has_amount) {
    return { label: 'Total of these heads', value: summary.value.new_amount, was: summary.value.current_amount }
  }

  return { label: 'Total Qty of these heads', value: summary.value.new_quantity, was: summary.value.current_quantity }
})

const totalDifference = computed(() => {
  const difference = totalTile.value.value - totalTile.value.was

  if (Math.abs(difference) < 0.005) return { text: 'no change', tone: 'hs-diff-none' }
  if (difference > 0) return { text: '+' + commaFormat(difference), tone: 'hs-diff-up' }

  return { text: '-' + commaFormat(Math.abs(difference)), tone: 'hs-diff-down' }
})

const editedCount = computed(() => new Set([...props.sheet.editedHeads, ...props.sheet.busyHeads]).size)

const hasUnsavedEdits = computed(() => props.sheet.step === 'review' && editedCount.value > 0)

const isChecking = computed(() => props.sheet.step === 'review' && props.sheet.busyHeads.length > 0)

const applyHint = computed(() => {
  if (props.sheet.errorCount > 0) return 'Correct the rows with a problem first.'
  if (props.sheet.changeCount < 1) return 'Nothing to apply. The file matches the saved budget.'

  return ''
})

const isStepDone = (index) => index < activeIndex.value || props.sheet.step === 'done'

const stepClass = (index) => {
  if (isStepDone(index)) return 'hs-step-done'
  if (index === activeIndex.value) return 'hs-step-active'

  return ''
}

const figure = (value) => {
  if (! value) return '0.00'

  return commaFormat(value)
}

const openModal = () => {
  props.sheet.reset()
  isOpen.value = true
}

const closeModal = () => {
  if (props.sheet.isApplying) return

  pendingAction.value = ''
  isOpen.value = false
  props.sheet.reset()
}

const leaveActions = {
  close: () => closeModal(),
  change: () => props.sheet.reset()
}

const confirmLeave = () => {
  const leave = leaveActions[pendingAction.value]

  pendingAction.value = ''

  if (! leave) return

  leave()
}

const keepEditing = () => {
  pendingAction.value = ''
}

const requestLeave = async (action) => {
  if (props.sheet.isApplying) return

  pendingAction.value = action

  if (! hasUnsavedEdits.value) {
    confirmLeave()
    return
  }

  await nextTick()

  if (! keepButton.value) return

  keepButton.value.focus()
}

const onKeydown = (event) => {
  if (! isOpen.value || event.key !== 'Escape') return

  if (pendingAction.value) {
    keepEditing()
    return
  }

  if (typingTags.includes(event.target.tagName)) return

  requestLeave('close')
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})

defineExpose({ openModal, closeModal })
</script>

<style scoped>
.head-sheet-modal {
  display: block;
  background: #1c1d1c33;
}

.hs-shell {
  background: #fbfcfe;
}

.hs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: .9rem 1.5rem;
  background: linear-gradient(135deg, #eef4ff 0%, #f6f9ff 100%);
  border-bottom: 1px solid #e6ebf5;
}

.hs-brand {
  display: flex;
  align-items: center;
  gap: .85rem;
  min-width: 0;
}

.hs-brand-icon {
  display: inline-flex;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #e3f4ea;
  color: #1d6f42;
  font-size: 1.25rem;
}

.hs-brand-text {
  min-width: 0;
}

.hs-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
}

.hs-subtitle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .25rem .6rem;
  margin-top: .3rem;
  font-size: .9rem;
  color: #4b5563;
}

.hs-tab-chip {
  padding: .1rem .6rem;
  border-radius: 999px;
  background: #7367f0;
  color: #fff;
  font-size: .8rem;
  font-weight: 600;
}

.hs-budget-name {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #212529;
  font-weight: 600;
}

.hs-stepper {
  display: flex;
  align-items: center;
  padding: 0;
  list-style: none;
}

.hs-step {
  display: flex;
  align-items: center;
  gap: .55rem;
  color: #6b7280;
  font-weight: 500;
  font-size: 1rem;
}

.hs-step + .hs-step::before {
  content: '';
  display: block;
  width: 48px;
  height: 2px;
  margin: 0 .75rem;
  border-radius: 2px;
  background: #dfe3ec;
}

.hs-step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #dfe3ec;
  background: #fff;
  font-size: .88rem;
  font-weight: 600;
}

.hs-step-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.hs-step-caption {
  font-size: .76rem;
  font-weight: 400;
  color: #6b7280;
}

.hs-step-active {
  color: #7367f0;
}

.hs-step-active .hs-step-dot {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(115, 103, 240, .15);
}

.hs-step-done {
  color: #28c76f;
}

.hs-step-done .hs-step-dot {
  border-color: #28c76f;
  background: #28c76f;
  color: #fff;
}

.hs-step + .hs-step-done::before,
.hs-step + .hs-step-active::before {
  background: #28c76f;
}

.hs-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e6ebf5;
  border-radius: 50%;
  background: #fff;
  color: #4b5563;
  transition: color .15s ease, border-color .15s ease;
}

.hs-close:not(:disabled):hover {
  border-color: #ea5455;
  color: #ea5455;
}

.hs-close:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.hs-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
}

.hs-narrow {
  width: 100%;
  max-width: 1100px;
  margin: 1.5rem auto 0;
}

.hs-muted {
  color: #4b5563;
}

.hs-done {
  padding: 2.5rem 1.5rem;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 24px rgba(34, 41, 47, .06);
  text-align: center;
}

.hs-done-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #e5f8ed;
  color: #28c76f;
  font-size: 2rem;
  box-shadow: 0 0 0 10px rgba(40, 199, 111, .07);
}

.hs-done-title {
  margin: 1.25rem 0 .35rem;
  font-weight: 600;
  color: #212529;
}

.hs-done-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  max-width: 720px;
  margin: 1.75rem auto;
}

.hs-done-tile {
  display: flex;
  flex-direction: column;
  gap: .2rem;
  padding: 1rem;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  background: #fbfcfe;
}

.hs-tile-label {
  font-size: .8rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.hs-tile-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #212529;
}

.hs-tile-diff {
  font-size: .9rem;
  font-weight: 600;
}

.hs-diff-up {
  color: #1f9d57;
}

.hs-diff-down {
  color: #ea5455;
}

.hs-diff-none {
  color: #6b7280;
}

.hs-tile-hint {
  font-size: .86rem;
  color: #6b7280;
}

.hs-done-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: .75rem;
}

.hs-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: .85rem 1.5rem;
  border-top: 1px solid #eef1f6;
  background: #fff;
  box-shadow: 0 -4px 16px rgba(34, 41, 47, .05);
}

.hs-file-chip {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  max-width: 100%;
  padding: .35rem .75rem;
  border: 1px solid #eef1f6;
  border-radius: 999px;
  background: #fbfcfe;
  font-size: .92rem;
}

.hs-file-icon {
  color: #1d6f42;
}

.hs-file-name {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #212529;
  font-weight: 500;
}

.hs-link {
  padding: 0;
  border: 0;
  background: none;
  color: #7367f0;
  font-weight: 500;
}

.hs-link:disabled {
  color: #b9b9c3;
}

.hs-footer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .75rem;
  margin-left: auto;
}

.hs-hint {
  font-size: .92rem;
  color: #e08a2e;
}

.hs-checking {
  display: inline-flex;
  align-items: center;
  font-size: .92rem;
  color: #7367f0;
}

.hs-apply {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
}

.hs-apply-count {
  min-width: 22px;
  padding: 0 .4rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, .25);
  font-size: .78rem;
  line-height: 20px;
}

.hs-confirm-backdrop {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(33, 37, 41, .35);
}

.hs-confirm {
  width: 100%;
  max-width: 440px;
  padding: 1.75rem 1.5rem 1.5rem;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(34, 41, 47, .22);
  text-align: center;
}

.hs-confirm-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff3e8;
  color: #ff9f43;
  font-size: 1.4rem;
}

.hs-confirm-title {
  margin: 1rem 0 .5rem;
  font-weight: 600;
  color: #212529;
}

.hs-confirm-text {
  font-size: .95rem;
  color: #4b5563;
}

.hs-confirm-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: .75rem;
  margin-top: 1.5rem;
}

@media (max-width: 767.98px) {
  .hs-header {
    flex-wrap: wrap;
    padding: .75rem 1rem;
  }

  .hs-stepper {
    order: 3;
    width: 100%;
    justify-content: center;
  }

  .hs-step-text {
    display: none;
  }

  .hs-budget-name {
    max-width: 180px;
  }

  .hs-step + .hs-step::before {
    width: 32px;
  }

  .hs-body {
    padding: 1rem;
  }

  .hs-narrow {
    margin-top: 0;
  }

  .hs-footer {
    padding: .75rem 1rem;
  }

  .hs-file-name {
    max-width: 180px;
  }
}
</style>
